import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    selectShowLiveRouteLabel,
    selectShowLiveRoutePopup,
    selectShowVehicleEid
} from '~/reducers/mapSettingsSlice';
import { selectSelectedMapRoutes } from '~/reducers/selectedMapRoutesSlice';

import constants from '~/utils/constants';
import { DriverMarkerPopup, Icon } from '~/ui/';

import './LiveRoutesMarker.scss';
import { useLiveRoutesUtils } from '~/hooks/useLiveRoutesUtils';

export function getExceptionIcons(driverData) {
    let rightPosition = 0;
    const extraIconRightShift = 0.4;
    const exceptionIcons = [];

    if (driverData.numAtRiskExceptions) {
        exceptionIcons.push({
            icon: 'delayFill',
            iconColor: 'saturn',
            position: `${rightPosition}rem`,
            value: driverData.numAtRiskExceptions
        });
        rightPosition += extraIconRightShift;
    }

    if (driverData.numLateExceptions) {
        exceptionIcons.push({
            icon: 'delayFill',
            iconColor: 'mars',
            position: `${rightPosition}rem`,
            value: driverData.numLateExceptions
        });
        rightPosition += extraIconRightShift;
    }

    if (driverData.numInventoryExceptions) {
        exceptionIcons.push({
            icon: 'warningFill3',
            iconColor: 'saturn',
            position: `${rightPosition}rem`,
            value: driverData.numInventoryExceptions
        });
    }

    return exceptionIcons;
}

function LiveRoutesMarker({ driverData, colorCSS }) {
    const { handleDriverSelect } = useLiveRoutesUtils();
    const { isSelectingMultipleCards } = useLiveRoutesUtils();
    const [showPopup, setShowPopup] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const showDriverLabel = useSelector(
        selectShowLiveRouteLabel(constants.mapRouteModes.DISPATCHED)
    );
    const showDriverPopup = useSelector(
        selectShowLiveRoutePopup(constants.mapRouteModes.DISPATCHED)
    );
    const showVehicleId = useSelector(
        selectShowVehicleEid(constants.mapRouteModes.DISPATCHED)
    );
    const { t } = useTranslation(['translation']);

    const { clientDriverId, name: driverName, vehicleEid } = driverData || {};
    const noEid = t('noEid');
    const driverMarkerLabel = showVehicleId ? vehicleEid || noEid : driverName;

    const selectedMapRoutes = useSelector(selectSelectedMapRoutes);

    useEffect(() => {
        // update when selecting multiple
        if (isSelectingMultipleCards) {
            setIsSelected(selectedMapRoutes.includes(clientDriverId));
            return;
        }

        // reset when selecting singles
        setIsSelected(false);
    }, [clientDriverId, isSelectingMultipleCards, selectedMapRoutes]);

    if (!driverData) {
        return null;
    }

    function _handleMouseEnter() {
        setShowPopup(showDriverPopup && true);
    }

    function _handleMouseLeave() {
        setShowPopup(false);
    }

    function _handleClick() {
        const toggleSelected = !isSelected;
        setIsSelected(toggleSelected);
        handleDriverSelect(toggleSelected, driverData);
    }

    function _getClassName() {
        let boxClassName = 'drivermarker';
        boxClassName =
            (isSelected && `${boxClassName} drivermarker_selected`) ||
            boxClassName;
        return boxClassName;
    }

    return (
        <button
            type="button"
            className={_getClassName()}
            onMouseEnter={_handleMouseEnter}
            onMouseLeave={_handleMouseLeave}
            onFocus={_handleMouseEnter}
            onClick={_handleClick}
            data-testid="driver-marker"
        >
            <div className="drivermarker_icon-group _p-relative">
                <span className="drivermarker-circle" style={colorCSS}>
                    {!isSelected && driverData.initials}
                    {isSelected && <Icon color="white" icon="checkmark" />}
                </span>
                {getExceptionIcons(driverData).map((iconData) => {
                    return (
                        <Icon
                            key={iconData.position}
                            className="drivermarker_icon _p-absolute"
                            icon={iconData.icon}
                            color={iconData.iconColor}
                            size="s"
                            style={{ right: iconData.position }}
                        />
                    );
                })}
            </div>
            {showDriverLabel && (
                <span className="drivermarker-label">{driverMarkerLabel}</span>
            )}
            {showPopup && (
                <DriverMarkerPopup
                    driverData={driverData}
                    exceptionData={getExceptionIcons(driverData)}
                />
            )}
        </button>
    );
}

export default LiveRoutesMarker;
